import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import {
    selectSupplierDetails,
    selectSupplierDetailsLoading,
    deleteSupplier,
    selectSupplierDeleteLoading,
    selectSupplierDeleteError,
    resetSupplierDetails
} from "../../redux/suppliers/suppliersSlice";
import { closeModal, setHasToRefresh, setModal } from "../../redux/rightModalSlice";
import LoadingSection from "../LoadingSection";

export default function ViewSupplier() {
    const dispatch = useDispatch();
    const supplier = useSelector(selectSupplierDetails);
    const supplierLoading = useSelector(selectSupplierDetailsLoading);
    const deleteLoading = useSelector(selectSupplierDeleteLoading);
    const deleteError = useSelector(selectSupplierDeleteError);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');

    // Gestisce il click sul bottone Modifica
    const handleEditClick = () => {
        dispatch(setModal({
            mode: 'edit',
            id: supplier._id
        }));
    };

    // Gestisce il click sul bottone Elimina
    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    // Conferma eliminazione fornitore
    const confirmDelete = () => {
        if (supplier?._id) {
            dispatch(deleteSupplier({
                supplierId: supplier._id,
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(closeModal());
                }
            });
        }
    };

    // Reset stato quando il componente viene smontato
    useEffect(() => {
        return () => {
            dispatch(resetSupplierDetails());
        };
    }, [dispatch]);

    if (supplierLoading) {
        return <LoadingSection />;
    }

    if (!supplier) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Fornitore non trovato</h2>
                <p className="text-gray-600">Il fornitore richiesto non è disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {deleteLoading && <LoadingSection isFixed />}

            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Dettagli Fornitore</h2>
                <div className="flex space-x-2">
                    <button
                        className="secondary"
                        onClick={handleEditClick}
                    >
                        <DynamicIcon iconClass="fas fa-edit" />
                        Modifica
                    </button>
                    <button
                        className="danger"
                        onClick={handleDeleteClick}
                    >
                        <DynamicIcon iconClass="fas fa-trash" />
                        Elimina
                    </button>
                </div>
            </div>

            {/* Dati Generali */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Dati Generali</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Codice</p>
                        <p className="text-base">{supplier.code || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Azienda</p>
                        <p className="text-base">{supplier.company || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Partita IVA</p>
                        <p className="text-base">{supplier.vatNumber || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Codice Fiscale</p>
                        <p className="text-base">{supplier.fiscalCode || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Codice SDI</p>
                        <p className="text-base">{supplier.sdiCode || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Stato</p>
                        <p className="text-base">
                            {supplier.active ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Attivo
                                </span>
                            ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Non attivo
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            {/* Indirizzo */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Indirizzo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Indirizzo</p>
                        <p className="text-base">{supplier.address || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Città</p>
                        <p className="text-base">{supplier.city || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Provincia</p>
                        <p className="text-base">{supplier.province || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">CAP</p>
                        <p className="text-base">{supplier.postalCode || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Nazione</p>
                        <p className="text-base">{supplier.state || "--"}</p>
                    </div>
                </div>
            </div>

            {/* Contatti */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Contatti</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Telefono</p>
                        <p className="text-base">{supplier.phone || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Cellulare</p>
                        <p className="text-base">{supplier.mobile || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Fax</p>
                        <p className="text-base">{supplier.fax || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Email</p>
                        <p className="text-base">{supplier.email || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">PEC</p>
                        <p className="text-base">{supplier.pec || "--"}</p>
                    </div>
                </div>
            </div>

            {/* Dati Bancari */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Dati Bancari</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Banca</p>
                        <p className="text-base">{supplier.bankName || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">IBAN</p>
                        <p className="text-base">{supplier.iban || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">ABI</p>
                        <p className="text-base">{supplier.abi || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">CAB</p>
                        <p className="text-base">{supplier.cab || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">SWIFT</p>
                        <p className="text-base">{supplier.swift || "--"}</p>
                    </div>
                </div>
            </div>

            {/* Condizioni Commerciali */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Condizioni Commerciali</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Tipo Pagamento</p>
                        <p className="text-base">{supplier.paymentLabel || supplier.paymentType || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">IVA</p>
                        <p className="text-base">{supplier.vatLabel || supplier.vatValue || "--"}</p>
                    </div>
                </div>
            </div>

            {/* Marchi */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Marchi</h3>
                <div>
                    {supplier.brands && supplier.brands.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {supplier.brands.map((brand, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                >
                                    {brand.brandLabel}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-base text-gray-400">Nessun marchio associato</p>
                    )}
                </div>
            </div>

            {/* Listini */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Listini</h3>
                <div>
                    {supplier.listini && supplier.listini.length > 0 ? (
                        <div className="space-y-3">
                            {supplier.listini.map((listino, index) => (
                                <div key={index} className="border border-gray-200 rounded-lg p-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 mb-2">
                                        {listino.listinoLabel}
                                    </span>
                                    {listino.files && listino.files.length > 0 ? (
                                        <div className="mt-2 space-y-1">
                                            {listino.files.map((file, fileIndex) => {
                                                const getIcon = (kind) => {
                                                    if (!kind) return 'fas fa-file';
                                                    if (kind.includes('pdf')) return 'fas fa-file-pdf';
                                                    if (kind.includes('sheet') || kind.includes('excel') || kind.includes('csv')) return 'fas fa-file-excel';
                                                    if (kind.includes('word') || kind.includes('document')) return 'fas fa-file-word';
                                                    if (kind.includes('image')) return 'fas fa-file-image';
                                                    return 'fas fa-file';
                                                };
                                                const getColor = (kind) => {
                                                    if (!kind) return 'text-gray-500';
                                                    if (kind.includes('pdf')) return 'text-red-500';
                                                    if (kind.includes('sheet') || kind.includes('excel') || kind.includes('csv')) return 'text-green-600';
                                                    if (kind.includes('word') || kind.includes('document')) return 'text-blue-600';
                                                    if (kind.includes('image')) return 'text-purple-500';
                                                    return 'text-gray-500';
                                                };
                                                return (
                                                    <a
                                                        key={fileIndex}
                                                        href={file.urlFile}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-2 py-1.5 px-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                                                    >
                                                        <DynamicIcon iconClass={getIcon(file.kind)} className={getColor(file.kind)} />
                                                        <span className="text-sm text-blue-600 hover:underline truncate">
                                                            {file.fileName}
                                                        </span>
                                                        <DynamicIcon iconClass="fas fa-download" className="text-gray-400 ml-auto text-xs" />
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400 mt-1">Nessun file allegato</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-base text-gray-400">Nessun listino associato</p>
                    )}
                </div>
            </div>

            {/* Agente */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold mb-3">Agente</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm font-medium text-gray-500">Nome Agente</p>
                        <p className="text-base">{supplier.agente || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Email Agente</p>
                        <p className="text-base">{supplier.agenteEmail || "--"}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500">Telefono Agente</p>
                        <p className="text-base">{supplier.agentePhone || "--"}</p>
                    </div>
                </div>
            </div>

            {/* Bottone Chiudi */}
            <div className="flex justify-end">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" />
                    Chiudi
                </button>
            </div>

            {/* Dialog conferma eliminazione */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Conferma Eliminazione</h3>
                        <p className="mb-6">
                            Sei sicuro di voler eliminare il fornitore "{supplier.company || supplier.code}"?
                            Questa azione non può essere annullata.
                        </p>

                        {deleteError && (
                            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                                <p>{deleteError}</p>
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                className="secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleteLoading}
                            >
                                Annulla
                            </button>
                            <button
                                className="danger"
                                onClick={confirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                        Eliminazione...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fas fa-trash" />
                                        Elimina
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
